import { redirect } from '@tanstack/react-router'

import { defaultSearchTimeRange } from './default-time-range'

import type { SearchParams } from './types'
import type { HistoryState } from '@tanstack/react-router'

/**
 * `remountDeps` for the Log Search route: remount on a Tenant switch.
 *
 * A switch only rewrites `search.t`, so the route stays matched and TanStack
 * Router keeps the page mounted. Sections outside an `ErrorBoundary` (time
 * range, query input, filters) would keep the previous Tenant's state. The
 * returned value becomes the route component's React key.
 */
export const searchRemountDeps = ({ search }: { search: { t?: string } }) =>
	search.t

/**
 * `beforeLoad` for the Log Search route: fill a missing `tr` before render.
 *
 * A Tenant switch drops `tr`, and a search without one throws. The fill is a
 * `replace` redirect, so it adds no Back step. A `tr` that failed validation
 * (non-ISO bounds) arrives here as `undefined` — see `searchSchema` — and gets
 * the default too. A preset `tr` counts as present.
 *
 * The redirect carries the navigation's history state: a redirect without
 * `state` gets an empty one, and a saved search with no `tr` would lose its
 * load id (`searchQueryLoadState`) and keep a stale working query.
 *
 * Not a Zod `.default()`: a time-based default changes on every validation and
 * never reaches the URL.
 */
export const ensureSearchTimeRange = ({
	search,
	location,
}: {
	search: SearchParams
	location: { pathname: string; state?: HistoryState }
}) => {
	if (search.tr) return

	throw redirect({
		to: location.pathname,
		search: { ...search, tr: defaultSearchTimeRange() },
		state: location.state,
		replace: true,
	})
}
